import { useCallback, useEffect, useRef, useState } from 'react'
import { loadOptionsForExistingPatient } from '../services/patientSearchService'
import { buildPatientDataForSelectingForNewBookingCalendar } from 'src/clinic/components/ClinicManageBookingsPage/newBookingCalendarUtils'
import { FORM_CONSTANTS } from '../utils/patientFormConfig'
import {
  findPrimaryPatientForRelated,
  isMakingRelatedPatientValid,
} from '../utils/patientValidationUtils'

export const usePatientSearch = (
  patientDetails,
  clinicId,
  clinicBrandId,
  addRelatedPatient,
  onFindingExistingPatient,
  setMakeAnonymousPatient,
  patientSearchApiSignature = '/api/secure/mentor/patient/list',
) => {
  const [showSelect, setShowSelect] = useState(false)
  const [mobileFocused, setMobileFocused] = useState(false)
  const [selectFocused, setSelectFocused] = useState(false)
  const [loadedPatientOptions, setLoadedPatientOptions] = useState([])
  const [isPaginationLoading, setIsPaginationLoading] = useState(false)

  const selectRef = useRef(null)
  const inputRef = useRef(null)
  const isLoadingRef = useRef(false)
  const lastSearchRef = useRef('')

  // Show/hide select dropdown based on mobile length
  useEffect(() => {
    setShowSelect(
      (patientDetails?.mobile?.length || 0) >= FORM_CONSTANTS.MIN_MOBILE_LENGTH_FOR_SEARCH,
    )
  }, [patientDetails?.mobile])

  const loadOptionsForPatients = useCallback(
    async (search, loadedOptions, addnl) => {
      const currentLoadedOptions = loadedOptions || []
      const isNewSearch = lastSearchRef.current !== search
      const isPagination = !isNewSearch && currentLoadedOptions.length > 0

      // Prevent concurrent requests
      if (isLoadingRef.current && isPagination) {
        return { 
          options: [], 
          hasMore: true, 
          additional: addnl 
        }
      }

      // For new search, reset everything
      if (isNewSearch) {
        lastSearchRef.current = search
        setLoadedPatientOptions([])
        setIsPaginationLoading(false)
      } else if (isPagination) {
        // Show loading indicator for pagination
        setIsPaginationLoading(true)
      }

      isLoadingRef.current = true

      try {
        const fetchedData = await loadOptionsForExistingPatient(search, currentLoadedOptions, addnl)
        const newOptions = fetchedData?.options || []
        const hasMore = fetchedData?.hasMore || false
        const additional = fetchedData?.additional || addnl

        // Update local state for display (this is just for UI tracking, not for the select component)
        setLoadedPatientOptions((prevOptions) => {
          if (isNewSearch) {
            return newOptions
          }
          if (isPagination && newOptions.length > 0) {
            const existingIds = new Set(prevOptions.map((opt) => opt.id))
            const uniqueNewOptions = newOptions.filter((opt) => !existingIds.has(opt.id))
            return [...prevOptions, ...uniqueNewOptions]
          }
          return newOptions.length > 0 ? newOptions : prevOptions
        })

        setIsPaginationLoading(false)
        isLoadingRef.current = false

        // Return exactly what react-select-async-paginate expects
        return {
          options: newOptions,
          hasMore: hasMore,
          additional: additional,
        }
      } catch (error) {
        console.error('Error loading patient options:', error)
        setIsPaginationLoading(false)
        isLoadingRef.current = false
        return { 
          options: [], 
          hasMore: false, 
          additional: addnl 
        }
      }
    },
    [],
  )

  const clearLoadedOptions = useCallback(() => {
    setLoadedPatientOptions([])
    setIsPaginationLoading(false)
    isLoadingRef.current = false
    lastSearchRef.current = ''
  }, [])

  const handleAddNewRelatedPatient = useCallback(
    (primaryPatient) => {
      const primaryPatientDataToUse =
        buildPatientDataForSelectingForNewBookingCalendar(primaryPatient)
      if (addRelatedPatient) {
        addRelatedPatient(primaryPatientDataToUse, patientDetails)
        setMobileFocused(false)
      }
    },
    [addRelatedPatient, patientDetails],
  )

  const isRelatedPatientValid = useCallback(
    (inputValue) => {
      return isMakingRelatedPatientValid(
        patientDetails,
        loadedPatientOptions,
        addRelatedPatient,
        inputValue,
      )
    },
    [patientDetails, loadedPatientOptions, addRelatedPatient],
  )

  const makeThisPatientRelated = useCallback(() => {
    if (!isRelatedPatientValid()) {
      return
    }

    const primaryPatient = findPrimaryPatientForRelated(patientDetails, loadedPatientOptions)
    if (primaryPatient) {
      handleAddNewRelatedPatient(primaryPatient)
    }
  }, [isRelatedPatientValid, patientDetails, loadedPatientOptions, handleAddNewRelatedPatient])

  const handlePhoneInputKeyDown = useCallback(
    (e) => {
      if (mobileFocused && loadedPatientOptions?.length > 0) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          if (selectRef.current) {
            selectRef.current.focus()
          }
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          if (isRelatedPatientValid()) {
            makeThisPatientRelated()
          } else {
            const firstOption = loadedPatientOptions?.[0]
            if (firstOption) {
              onFindingExistingPatient(firstOption)
            }
          }
        }
      }
    },
    [
      mobileFocused,
      loadedPatientOptions,
      isRelatedPatientValid,
      makeThisPatientRelated,
      onFindingExistingPatient,
    ],
  )

  const handleMobileFocus = useCallback(() => {
    setMobileFocused(true)
  }, [])

  const handleMobileBlur = useCallback(() => {
    setMobileFocused(false)
  }, [])

  const handleSelectFocus = useCallback(() => {
    setSelectFocused(true)
  }, [])

  const handleSelectBlur = useCallback(() => {
    setSelectFocused(false)
  }, [])

  const handlePatientSelect = useCallback(
    (patient) => {
      clearLoadedOptions()
      onFindingExistingPatient(patient)
    },
    [clearLoadedOptions, onFindingExistingPatient],
  )

  const handleInputChange = useCallback((value, inputRef) => {
    if (inputRef?.current) {
      inputRef.current?.focus()
    }
    if (selectRef.current) {
      selectRef.current.blur()
    }
  }, [])

  const shouldShowSearchDropdown = useCallback(() => {
    return (
      showSelect &&
      !patientDetails?.disablePatientSearch &&
      (mobileFocused || selectFocused) &&
      clinicId &&
      clinicBrandId
    )
  }, [
    showSelect,
    patientDetails?.disablePatientSearch,
    mobileFocused,
    selectFocused,
    clinicId,
    clinicBrandId,
  ])

  const getSearchConfig = useCallback(() => {
    return {
      clinicId,
      clinicBrandId,
      patientSearchApiSignature,
      inputValue: patientDetails?.mobile,
    }
  }, [clinicId, clinicBrandId, patientSearchApiSignature, patientDetails?.mobile])

  const resetSearchState = useCallback(() => {
    setShowSelect(false)
    setMobileFocused(false)
    setSelectFocused(false)
    setIsPaginationLoading(false)
    isLoadingRef.current = false
    lastSearchRef.current = ''
    clearLoadedOptions()
  }, [clearLoadedOptions])

  return {
    // State
    showSelect,
    mobileFocused,
    selectFocused,
    loadedPatientOptions,
    isPaginationLoading,

    // Refs
    selectRef,
    inputRef,

    // Functions
    loadOptionsForPatients,
    clearLoadedOptions,
    handleAddNewRelatedPatient,
    isRelatedPatientValid,
    makeThisPatientRelated,
    handlePhoneInputKeyDown,
    handleMobileFocus,
    handleMobileBlur,
    handleSelectFocus,
    handleSelectBlur,
    handlePatientSelect,
    handleInputChange,
    shouldShowSearchDropdown,
    getSearchConfig,
    resetSearchState,

    // Utilities
    formatCreateLabel: () => 'Add related patient for this number',
    isValidNewOption: isRelatedPatientValid,
    onCreateOption: makeThisPatientRelated,
  }
}
